import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Send, ExternalLink, FileText, Clock, Check, CheckCheck, AlertCircle, Trash2 } from "lucide-react";
import { api } from "@/lib/api";
import { waLink } from "@/lib/whatsapp";
import WaTemplateSender from "@/components/whatsapp/WaTemplateSender";
import WaQuickReplies from "@/components/whatsapp/WaQuickReplies";
import WaConvAvatar from "@/components/whatsapp/WaConvAvatar";
import WaRegisterCustomer from "@/components/whatsapp/WaRegisterCustomer";
import { UserPlus, UserCheck, MapPin } from "lucide-react";

const StatusIcon = ({ s }) => {
    if (s === "read") return <CheckCheck className="w-3.5 h-3.5 text-sky-500" />;
    if (s === "delivered") return <CheckCheck className="w-3.5 h-3.5" />;
    if (s === "failed") return <AlertCircle className="w-3.5 h-3.5 text-red-500" />;
    return <Check className="w-3.5 h-3.5" />;
};

export default function WaChat({ conv, apiReady, onChanged, onDeleted, quickReplies, onQuickRepliesSaved }) {
    const [msgs, setMsgs] = useState([]);
    const [text, setText] = useState("");
    const [sending, setSending] = useState(false);
    const [avatar, setAvatar] = useState(conv.avatar_url);
    const [registering, setRegistering] = useState(false);
    const endRef = useRef(null);
    const isWeb = conv.phone.startsWith("web-");

    const load = () => api.get(`/admin/whatsapp/conversations/${conv.phone}/messages`).then((r) => setMsgs(r.data));
    useEffect(() => {
        load();
        const t = setInterval(load, 5000);
        return () => clearInterval(t);
    }, [conv.phone]); // eslint-disable-line react-hooks/exhaustive-deps
    useEffect(() => {
        if (conv.phone.startsWith("web-")) return;
        api.post(`/admin/whatsapp/conversations/${conv.phone}/avatar`).then((r) => { if (r.data.avatar_url !== conv.avatar_url) { setAvatar(r.data.avatar_url); onChanged(); } }).catch(() => null);
    }, [conv.phone]); // eslint-disable-line react-hooks/exhaustive-deps
    // Scroll only the messages box (never the page) so the screen stays where it is
    useEffect(() => { const el = endRef.current?.parentElement; if (el) el.scrollTop = el.scrollHeight; }, [msgs.length]);

    const send = async (e, override) => {
        e?.preventDefault();
        const body = (override ?? text).trim();
        if (!body) return;
        if (!apiReady || !conv.window_open) {
            window.open(waLink(conv.phone, body), "_blank", "noopener");
            if (!override) setText("");
            return;
        }
        setSending(true);
        try {
            await api.post("/admin/whatsapp/send/text", { phone: conv.phone, text: body });
            setText("");
            await load();
            onChanged();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Dërgimi dështoi");
        } finally {
            setSending(false);
        }
    };

    const sendLocation = async () => {
        if (!apiReady || !conv.window_open) {
            const win = window.open("", "_blank");
            try {
                const { data } = await api.get("/admin/whatsapp/location");
                const link = waLink(conv.phone, `📍 ${data.name}${data.address ? ` – ${data.address}` : ""}\n${data.maps_url}`);
                if (win) win.location.href = link;
            } catch (err) {
                win?.close();
                toast.error(err?.response?.data?.detail || "Lokacioni mungon");
            }
            return;
        }
        setSending(true);
        try {
            await api.post("/admin/whatsapp/send/location", { phone: conv.phone });
            await load();
            onChanged();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Dërgimi dështoi");
        } finally {
            setSending(false);
        }
    };

    const remove = async () => {
        if (!window.confirm("Fshi këtë bisedë?")) return;
        await api.delete(`/admin/whatsapp/conversations/${conv.phone}`);
        onDeleted();
    };

    return (
        <div className="flex flex-col h-[70vh] min-h-[420px]" data-testid="wa-chat">
            <div className="flex items-center justify-between gap-3 px-4 py-3 border-b border-slate-200">
                <div className="min-w-0 flex items-center gap-3">
                    <WaConvAvatar conv={{ ...conv, avatar_url: avatar }} size={42} />
                    <div className="min-w-0">
                    <div className="font-display font-black truncate" data-testid="wa-chat-name">{conv.name || (isWeb ? "Vizitor web" : `+${conv.phone}`)}</div>
                    <div className="text-[11px] font-mono text-slate-500 flex items-center gap-2">
                        {!isWeb && `+${conv.phone}`}
                        {conv.source === "web" && <span className="px-1.5 rounded bg-blue-100 text-blue-800">nga faqja</span>}
                        {apiReady && !isWeb && (
                            <span className={`flex items-center gap-1 px-1.5 rounded ${conv.window_open ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`} data-testid="wa-window-status">
                                <Clock className="w-3 h-3" /> {conv.window_open ? "Dritarja 24h e hapur" : "Jashtë 24h – vetëm template"}
                            </span>
                        )}
                    </div>
                    </div>
                </div>
                <div className="flex items-center gap-1">
                    {!isWeb && !conv.phone.startsWith("lid-") && (conv.customer ? (
                        <span className="hidden sm:flex items-center gap-1 text-[10px] font-mono font-bold uppercase px-2 py-1 rounded bg-emerald-100 text-emerald-700" data-testid="wa-linked-customer"><UserCheck className="w-3 h-3" /> Klient</span>
                    ) : (
                        <button onClick={() => setRegistering(true)} className="btn-outline !py-1.5 text-xs" data-testid="wa-register-btn"><UserPlus className="w-3.5 h-3.5" /> Regjistro klient</button>
                    ))}
                    {!isWeb && (
                        <a href={waLink(conv.phone)} target="_blank" rel="noreferrer" className="btn-outline !py-1.5 text-xs" data-testid="wa-open-external"><ExternalLink className="w-3.5 h-3.5" /> Hap në WhatsApp</a>
                    )}
                    <button onClick={remove} className="p-2 text-slate-500 hover:text-red-600" data-testid="wa-delete-conv"><Trash2 className="w-4 h-4" /></button>
                </div>
            </div>
            <div className="flex-1 overflow-y-auto p-4 space-y-2 wa-chat-bg" data-testid="wa-messages">
                {msgs.length === 0 && <div className="text-center text-xs font-mono text-slate-500 py-10">Asnjë mesazh akoma.</div>}
                {msgs.map((m) => (
                    <div key={m.id} className={`flex ${m.direction === "out" ? "justify-end" : "justify-start"}`}>
                        <div className={`max-w-[75%] rounded-xl px-3 py-2 text-sm shadow-sm ${m.direction === "out" ? "wa-bubble-out rounded-tr-none" : "wa-bubble-in rounded-tl-none"}`} data-testid={`wa-msg-${m.id}`}>
                            {m.type === "document" && m.doc_url && (
                                <a href={m.doc_url} target="_blank" rel="noreferrer" className="flex items-center gap-2 underline mb-1"><FileText className="w-4 h-4" /> {m.filename || "Dokument"}</a>
                            )}
                            {m.image_url && (
                                <a href={m.image_url} target="_blank" rel="noreferrer" className="block mb-1" data-testid={`wa-msg-image-${m.id}`}>
                                    <img src={m.image_url} alt="foto" className="rounded-lg max-h-72 w-auto object-cover" loading="lazy" />
                                </a>
                            )}
                            {m.type === "location" && m.lat != null ? (
                                <a href={`https://maps.google.com/?q=${m.lat},${m.lng}`} target="_blank" rel="noreferrer" className="flex items-start gap-2 underline" data-testid={`wa-msg-location-${m.id}`}>
                                    <MapPin className="w-4 h-4 mt-0.5 shrink-0 text-red-500" /> <span className="whitespace-pre-wrap break-words">{m.text}</span>
                                </a>
                            ) : (
                                !(m.image_url && m.text === "[foto]") && <div className="whitespace-pre-wrap break-words">{m.text}</div>
                            )}
                            <div className="flex items-center justify-end gap-1 mt-1 text-[10px] opacity-70">
                                {m.type === "web" && <span>web ·</span>}
                                {new Date(m.created_at).toLocaleString([], { dateStyle: "short", timeStyle: "short" })}
                                {m.direction === "out" && <StatusIcon s={m.status} />}
                            </div>
                            {m.error && <div className="text-[10px] text-red-600 mt-0.5">{m.error}</div>}
                        </div>
                    </div>
                ))}
                <div ref={endRef} />
            </div>
            {!isWeb && apiReady && !conv.window_open && <WaTemplateSender phone={conv.phone} onSent={() => { load(); onChanged(); }} />}
            {!isWeb && <WaQuickReplies items={quickReplies} onPick={(q) => (q.kind === "location" ? sendLocation() : send(null, q.text))} onSaved={onQuickRepliesSaved} />}
            {!isWeb && (
                <form onSubmit={send} className="flex items-end gap-2 p-3 border-t border-slate-200">
                    <textarea
                        rows="1"
                        value={text}
                        onChange={(e) => setText(e.target.value)}
                        onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) send(e); }}
                        placeholder={apiReady && conv.window_open ? "Shkruani mesazhin…" : "Shkruani dhe hapet WhatsApp-i juaj…"}
                        className="input-field flex-1 resize-none"
                        data-testid="wa-composer"
                    />
                    <button disabled={sending || !text.trim()} className="w-11 h-11 rounded-full bg-[#25D366] text-white grid place-items-center shrink-0 disabled:opacity-50" data-testid="wa-send">
                        <Send className="w-4 h-4" />
                    </button>
                </form>
            )}
            {registering && <WaRegisterCustomer conv={conv} onClose={() => setRegistering(false)} onDone={() => { setRegistering(false); onChanged(); }} />}
            {isWeb && <div className="p-3 border-t border-slate-200 text-xs text-slate-500">Vizitori nuk la numër. Mesazhi i tij hapet edhe në WhatsApp-in tuaj nëse e dërgoi.</div>}
        </div>
    );
}
